import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIcon } from '../../icons/appIcons.tsx';

export default function DonatePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    amount: '',
    customAmount: '',
    message: '',
    paymentMethod: 'card'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const presetAmounts = [25, 50, 100, 250, 500];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear custom amount if preset is selected
      ...(name === 'amount' && value !== 'custom' ? { customAmount: '' } : {})
    }));
  };

  const handleAmountSelect = (amount: number) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString(),
      customAmount: ''
    }));
  };

  const handleCustomAmountSelect = () => {
    setFormData(prev => ({
      ...prev,
      amount: 'custom'
    }));
  };

  const getFinalAmount = () => {
    if (formData.amount === 'custom') {
      return parseFloat(formData.customAmount) || 0;
    }
    return parseFloat(formData.amount) || 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const finalAmount = getFinalAmount();

    if (finalAmount < 1) {
      setError('Please enter a donation amount of at least $1');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/donations/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          amount: finalAmount,
          message: formData.message,
          paymentMethod: formData.paymentMethod
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          amount: '',
          customAmount: '',
          message: '',
          paymentMethod: 'card'
        });
      } else {
        setError(result.message || 'Failed to process donation. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Donation error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ggaa-light to-white p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto bg-green-100 rounded-full p-4 w-20 h-20 flex items-center justify-center">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-2">
            Your generous donation has been recorded and will help support God's Green Acre Association.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Note: This is a placeholder submission. Actual payment processing will be integrated with Stripe in the future.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setSuccess(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  amount: '',
                  customAmount: '',
                  message: '',
                  paymentMethod: 'card'
                });
              }}
              className="w-full btn-primary"
            >
              Make Another Donation
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ggaa-light to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/images/ggaa-logo.png" 
                alt="GGAA Logo" 
                className="h-12 w-auto mr-4"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold text-ggaa-dark font-ranch">God's Green Acre Association</h1>
                <p className="text-sm text-gray-600">Support Our Mission</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-ggaa-primary hover:text-ggaa-dark font-medium"
            >
              Member Login →
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Make a Donation</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your generous support helps us provide exceptional experiences for our ranch girls and maintain our facilities. Every donation makes a difference!
          </p>
        </div>

        {error && (
          <div className="mb-6 max-w-2xl mx-auto p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
              {error}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Donation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Donation Amount */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Donation Amount</h3>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                    {presetAmounts.map(amount => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleAmountSelect(amount)}
                        className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                          formData.amount === amount.toString()
                            ? 'border-ggaa-primary bg-ggaa-primary text-white'
                            : 'border-gray-300 text-gray-700 hover:border-ggaa-primary hover:bg-ggaa-light'
                        }`}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={handleCustomAmountSelect}
                      className={`w-full mb-2 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                        formData.amount === 'custom'
                          ? 'border-ggaa-primary bg-ggaa-primary text-white'
                          : 'border-gray-300 text-gray-700 hover:border-ggaa-primary hover:bg-ggaa-light'
                      }`}
                    >
                      Custom Amount
                    </button>
                    {formData.amount === 'custom' && (
                      <div className="mt-2">
                        <label htmlFor="customAmount" className="block text-sm font-medium text-gray-700 mb-2">
                          Enter Custom Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                          <input
                            type="number"
                            id="customAmount"
                            name="customAmount"
                            min="1"
                            step="0.01"
                            required
                            value={formData.customAmount}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                  >
                    <option value="card">Credit/Debit Card (via Stripe - Coming Soon)</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    Stripe integration is coming soon. For now, this will record your donation intent.
                  </p>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Share why you're supporting GGAA..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ggaa-primary focus:border-transparent"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>Donate ${getFinalAmount().toFixed(2)}</>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-ggaa-primary/10 rounded-2xl p-6 border-2 border-ggaa-primary/20">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Donate?</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <AppIcon name="horse" className="w-5 h-5 text-ggaa-primary mr-2 shrink-0 mt-0.5" />
                  <span>Support horse care and facility maintenance</span>
                </li>
                <li className="flex items-start">
                  <AppIcon name="ranchGirl" className="w-5 h-5 text-ggaa-primary mr-2 shrink-0 mt-0.5" />
                  <span>Fund programs for ranch girls</span>
                </li>
                <li className="flex items-start">
                  <AppIcon name="directory" className="w-5 h-5 text-ggaa-primary mr-2 shrink-0 mt-0.5" />
                  <span>Provide educational opportunities</span>
                </li>
                <li className="flex items-start">
                  <AppIcon name="girlsCorner" className="w-5 h-5 text-ggaa-primary mr-2 shrink-0 mt-0.5" />
                  <span>Help maintain our beautiful facilities</span>
                </li>
              </ul>
              
              <div className="mt-6 pt-6 border-t border-ggaa-primary/20">
                <h4 className="font-semibold text-gray-900 mb-2">Tax Information</h4>
                <p className="text-xs text-gray-600">
                  GGAA is a registered non-profit organization. All donations may be tax-deductible. You will receive a receipt via email.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-ggaa-primary/20">
                <h4 className="font-semibold text-gray-900 mb-2">Questions?</h4>
                <p className="text-xs text-gray-600 mb-2">
                  Contact us at:
                </p>
                <a href="mailto:donations@ggaa.org" className="text-xs text-ggaa-primary hover:text-ggaa-dark font-medium">
                  donations@ggaa.org
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>© 2024 God's Green Acre Association. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
} 